import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import HorizontalSpinner from './components/HorizontalSpinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Carousel from 'react-multi-carousel'
import { Button, Collapse, Modal } from 'antd'
import { useForm } from 'react-hook-form'
import cn from 'classnames'
import Turnstile, { useTurnstile } from 'react-turnstile'
import Swal from 'sweetalert2'
import { getUrlParameter } from './utils'
import 'react-multi-carousel/lib/styles.css'
import { apiRootUrl } from './Api'
import './cookieconsent.css'
import * as CookieConsent from 'vanilla-cookieconsent'
import cookieConsentConfig from './cookieConsentConfig'
const turnstileKey = import.meta.env.VITE_TURNSTILE_PUBLICKEY

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
  }
}

const items = [
  {
    key: '1',
    label: 'No Start Up Costs',
    children: <p>No set up fees, software costs, annual maintenance fees or other hidden costs...absolutely risk-free.</p>,
  },
  {
    key: '2',
    label: 'Low, Low Service Fees',
    children: <p>Our service fees are among the lowest in the ticketing industry. Our low service fees result in increased affordability for your ticket buyers and increased attendance for your events.</p>,
  },
  {
    key: '3',
    label: 'Easy To Use',
    children: <p>With our intuitive ticket sales software, training is a snap. Your box office will be up to speed in no time.</p>,
  },
  {
    key: '4',
    label: 'Mobile Ticket Sales and Barcode Scanning',
    children: <p>Sell tickets from your iPhone, iPad or other mobile device. You can also scan barcodes at real-time using your iPhone, iPod Touch or iPad.</p>,
  },
  {
    key: '5',
    label: '24/7 Service and Support',
    children: <p>We make ourselves available to you 24/7, 365 days a year. It's not lip service - it's a promise.</p>,
  },
  {
    key: '6',
    label: 'Satisfaction Guaranteed',
    children: <p>We won't lock you into a long-term contract. We put ourselves in a position to earn your business, and we will.</p>,
  },
]

function Home() {
  const [modalOpen, setModalOpen] = useState(null)
  const { register, handleSubmit, formState: { errors } } = useForm()
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [infoRequestLoading, setInfoRequestLoading] = useState(false)
  const turnstile = useTurnstile()

  useEffect(() => {
    var subdomain = window.location.host.split('.')[1] ? window.location.host.split('.')[0] : false;
    if (subdomain && subdomain !== 'www') {
      fetch(apiRootUrl + '/api/venuemanagement/redirecturl/' + subdomain)
      .then((response) => {
          return response.json()
      })
      .then((data) => {
          if (data.payload) {
              window.location.replace('https://www.tix.com' + data.payload);
          }
      })
    }
    CookieConsent.run(cookieConsentConfig)
  }, [])

  const submitInfoRequest = async (data) => {
    setInfoRequestLoading(true)
    data.Token = turnstileToken
    var source = getUrlParameter('src')
    data.Source = source
    const response = await fetch(apiRootUrl + '/api/informationrequest/request', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })

    if (response.ok) {
      turnstile.reset()
      gtag('event', 'InfoRequestSubmit')
      Swal.fire({
        title: 'Thank You, ' + data.FirstName + '!',
        text: modalOpen === 'learnMore' ? 'We will be in touch shortly to answer any questions you might have about how Tix can bring success to your events.' : 'We look forward to working with you and will be in touch shortly to help you set up your Tix account.',
        icon: 'success',
        confirmButtonText: 'OK'
      })
      setModalOpen(null)
      setInfoRequestLoading(false)
    } else {
      let challengeBypassHeader = response.headers.get('cf-chl-bypass')
      if (response.status === 403 && challengeBypassHeader !== null) {
          window.location.reload()
      }
      setInfoRequestLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Tix - Software and services for online and box office ticket sales</title>
        <link rel='canonical' href={window.location.href} />
      </Helmet>
      <div>
      <Modal title='Easy Integration' 
        open={modalOpen === '1'} 
        onCancel={() => setModalOpen(null)} 
        footer={[
          <Button key='back' onClick={() => setModalOpen(null)}>
            Close
          </Button>
        ]}>
        <div className='modal-body'>
          <p>
            <strong>Credit Card Processing</strong>
            <br />
            <strong>Bring your own</strong> -Tix is compatible with over 30
            different e-commerce payment gateways including Authorize.net,
            Cybersource, Global Gateway, Virtual Merchant, Stripe, PayPal and
            many more. Tix does not charge an additional fee if you use your own
            merchant account. When using your merchant account for credit card
            processing, ticket revenue goes directly into your account within
            48-72 hours after each ticket purchase.
            <br />
            <br />
            <strong>Let Tix Handle it</strong> - If you do not have the time, or
            if you do not wish to incur the expense of setting up your own
            merchant account, Tix can handle the credit card processing for your
            organization. If you wish to use the Tix merchant account we charge
            an additional fee of 5% of the ticket price for credit card
            processing. When using our merchant account for credit card
            processing, we send you a check the week after the event.
          </p>
          <p>
            <strong>Ticket Printing</strong>
            <br />
            <strong>E-Tickets</strong> - Let your ticket buyers print their own
            tickets at home. Each E-Ticket is uniquely barcoded for added peace
            of mind.
          </p>
          <p>
            <strong>Thermal Ticket Printing</strong> - Tix interfaces with BOCA,
            Practical Automation and Datamax thermal ticket printers. Print
            tickets on demand in advance and in the box office.
          </p>
          <p>
            <strong>Pre-printed Tickets</strong> - Don't have your own ticket
            printer? Let Tix print tickets for you. We can print a subset of
            tickets or the whole house.{' '}
          </p>
          <p>
            <strong>Other options</strong> - Tix also supports some thermal
            receipt printers, standard inkjet/laser printers and can support a
            ticketless Will Call option.{' '}
          </p>
        </div>
      </Modal>

      <Modal title='Sell Tickets' 
        open={modalOpen === '2'} 
        onCancel={() => setModalOpen(null)} 
        footer={[
          <Button key='back' onClick={() => setModalOpen(null)}>
            Close
          </Button>
        ]}>
          <div className='modal-body'>
            <p>
              <strong>ONLINE</strong>
              <br />
              <strong>Private Label</strong> - Tix seamlessly integrates ticket
              purchasing into your existing web site.
            </p>
            <p>
              <strong>Facebook Sales</strong> - We'll help you configure your
              Facebook account to sell tickets directly on your Facebook page. You
              can also link to your customized Tix ticket sales page from Facebook
              via a “Buy Tickets” button.
            </p>
            <p>
              <strong>Mobile Friendly</strong> - Mobile ticket sales are
              increasing at a rapid pace. Tix has a mobile friendly site to insure
              your patrons can purchase your tickets on mobile devices and
              tablets.{' '}
            </p>
            <p>
              <strong>E-Tickets/Print @ Home Tickets</strong> - Allow your ticket
              buyers to print their own tickets saving you time and money and
              freeing your box office staff up to sell more tickets.{' '}
            </p>
            <p>
              <strong>Secure</strong> - Tix employs 128 bit Secure Sockets Layer
              (SSL) Encryption to make sure that sensitive information is secure
              as it's transmitted to and from your ticket buyer and the credit
              card processors.
            </p>
            <p>
              <strong>BOX OFFICE</strong>
              <br />
              <strong>Easy to Use</strong> - With our intuitive ticket sales
              software, training is a snap. Your box office will be up to speed in
              no time.
            </p>
            <p>
              <strong>Touch Screen Compatible</strong> - Take advantage of our
              touch screen program to speed up your walk-up transactions, free up
              counter space and reduce the amount of hardware required.{' '}
            </p>
            <p>
              <strong>Card Swipe</strong> - Streamline your walk-up ticket sales
              with compatible credit card swipes.
            </p>
          </div>
        </Modal>

        <Modal title='Multiple Seating Options' 
          open={modalOpen === '3'} 
          onCancel={() => setModalOpen(null)} 
          footer={[
            <Button key='back' onClick={() => setModalOpen(null)}>
              Close
            </Button>
          ]}>
          <div className='modal-body'>
            <p>
              <strong>Seat Selection</strong> - Patrons love choosing their own
              seats using our interactive graphical seat maps. Different patrons
              have different seating preferences. Tix allows you to accommodate
              them while taking the load off of your box office staff.Tix has
              1000's of seat maps on file. If we don't have it, we will program it
              for you free of charge.
            </p>
            <p>
              <strong>Best Available</strong> - Tix can be configured to assign
              best availability seating by price tier based on the seating
              priorities you specify.
            </p>
            <p>
              <strong>Photographic Seat View</strong> - Show your ticket buyer
              what the view from their seats will look like! Tix gives your
              patrons the ability to view photographs from each of the seat
              locations giving them better tools to insure they'll enjoy your
              events and buy more tickets.{' '}
            </p>
            <p>
              <strong>Unlimited</strong> - Our system supports an unlimited number
              of venues and seating configurations for your organization.
            </p>
            <p>
              <strong>Flexible</strong> - Tix supports complex seating
              arrangements, including venues with combined reserved and general
              admission seating.
            </p>
          </div>
        </Modal>
      
        <Modal title='CRM (Customer Relationship Management)' 
          open={modalOpen === '4'} 
          onCancel={() => setModalOpen(null)} 
          footer={[
            <Button key='back' onClick={() => setModalOpen(null)}>
              Close
            </Button>
          ]}>
              <div className='modal-body'>
                <p>
                  Generate customer lists based on purchase history or entry date into
                  the customer database. Customer information (name, address, phone
                  number, email address, customer classifications) can be updated at
                  real-time and our Merge Customer History program allows authorized
                  users to manage any duplicate customer records that have been
                  created.
                </p>
                <p>
                  Discover your most important patrons - Who buys the most tickets?
                  Who are your most important donors?
                </p>
                <p>
                  Keep first time buyers coming back - Instantly generate a list of
                  first time buyers and invite them back!
                </p>
                <p>
                  Stay connected with donors, members, subscribers and repeat buyers
                </p>
                <p>Identify and create groups of different customer types</p>
                <p>
                  Detailed customer history - Develop segments based on past purchase
                  history
                </p>
              </div>
        </Modal>
   
        <Modal title='Engage Your Patrons' 
          open={modalOpen === '5'} 
          onCancel={() => setModalOpen(null)} 
          footer={[
            <Button key='back' onClick={() => setModalOpen(null)}>
              Close
            </Button>
          ]}>      
          <div className='modal-body'>
            <p>
              <strong>Social</strong> - Post your event to{' '}
              <strong>Facebook</strong> and generate buzz about your next event.
              Tix makes it easy to post your events on your{' '}
              <strong>Facebook</strong> Fan pages and allow your ticket buyers to
              share their purchase and information about your event on their
              social networking pages including <strong>Facebook</strong> and{' '}
              <strong>Twitter</strong>.{' '}
            </p>
            <p>
              <strong>Surveys</strong> - We can customize demographic data
              collection to suit your needs. We can collect anything from the
              simple 'How did you hear about us?' to sophisticated surveys with
              multiple choice and free-form answers.
            </p>
            <p>
              <strong>Donations</strong> - You can solicit and accept donations
              and memberships for your organization. Tix allows you to offer
              pre-set levels and/or open donation amounts. Quickly and easily
              generate donation reports and use that information to thank your
              donors for supporting your organization and tell them why they
              matter.{' '}
            </p>
            <p>
              <strong>QR Codes</strong> - Easily generate QR codes for use in
              marketing communications. Scanning the code with a mobile device
              takes the ticket buyer directly to a mobile optimized ticket sales
              page.
            </p>
            <p>
              <strong>Discount Codes</strong> - Discount codes give your ticket
              buyers an incentive to purchase tickets. Provide an amount or
              percentage off each ticket. Encourage patrons to bring their friends
              with group discounts. Improve advanced sales with limited-time
              promotions.{' '}
            </p>
            <p>
              <strong>Renewals</strong> - Stay in touch with your subscribers and
              make it easy for them to renew their package each season with our
              automated package renewal process.
            </p>
          </div>
        </Modal>
        
        <Modal title='Email Marketing' 
          open={modalOpen === '6'} 
          onCancel={() => setModalOpen(null)} 
          footer={[
            <Button key='back' onClick={() => setModalOpen(null)}>
              Close
            </Button>
          ]}>
            <div className='modal-body'>
              <p>
                Tix makes communicating with your ticket buyers easy. Tix can be
                used to send an email blast to past ticket buyers letting them know
                about an upcoming event. Our e-mail notification program can also be
                used to inform patrons of important information such as a
                cancellation, postponement, line-up change, or parking information
                related to an event they've already purchased tickets to.
              </p>
              <p>
                Email notifications are logged and communication history can be
                viewed when viewing customer details.{' '}
              </p>
            </div>
          </Modal>

        <Modal title='Donations and Memberships' 
          open={modalOpen === '7'} 
          onCancel={() => setModalOpen(null)} 
          footer={[
            <Button key='back' onClick={() => setModalOpen(null)}>
              Close
            </Button>
          ]}>
            <div className='modal-body'>
              <p>
                The donation/membership solicitation can be programmed to appear
                during the ticket purchase process. You can also link directly to
                the donation/membership solicitation so that your patrons can
                contribute to your organization independent of making a ticket
                purchase.
              </p>
              <p>
                Tix offers the ability to add and track sponsor and donor
                information in a manner that reduces unnecessary work and
                facilitates revenue generation for your organization.
              </p>
              <p>
                Pledge reminders to donors can be created in the Tix system for
                automatic follow-up.
              </p>
              <p>
                Tix supports customer donations and membership renewals online and
                through the box office system.
              </p>
            </div>
          </Modal>

          <Modal title='Mobile Box Office' 
            open={modalOpen === '8'} 
            onCancel={() => setModalOpen(null)} 
            footer={[
              <Button key='back' onClick={() => setModalOpen(null)}>
                Close
              </Button>
            ]}>
            <div className='modal-body'>
              <p>
                Mobile ticket sales are increasing at a rapid pace. Tix has a mobile
                friendly site to ensure your patrons can purchase tickets to your
                events on their mobile devices and tablets.
              </p>
              <p>
                Our mobile-optimized ticket sales pages allow your patrons to easily
                complete their ticket orders on their mobile device or tablet.
              </p>
              <p>
                Your patrons can still access all of the ticket-buying options
                available on our mobile-optimized ticket sales pages, including the
                abilty for them to select a seat.
              </p>
            </div>
          </Modal>

          <Modal title='Season Packages' 
            open={modalOpen === '9'} 
            onCancel={() => setModalOpen(null)} 
            footer={[
              <Button key='back' onClick={() => setModalOpen(null)}>
                Close
              </Button>
            ]}>  
              <div className='modal-body'>
                <p>Tix offers various season ticket/package options, including:</p>
                <p>
                  <strong>Fixed Season Packages</strong> where the patron gets the
                  same seat across all of the shows in the package.
                </p>
                <p>
                  <strong>Build Your Own Packages</strong> where the patron selects
                  their own seat for all of the shows included in the package and then
                  receives a discount at checkout.
                </p>
                <p>
                  <strong>Flexible Packages (Flex Pass)</strong> where the patron gets
                  a voucher for each of the events included in the package. The
                  vouchers can then be exchanged for individual event tickets through
                  the box office.
                </p>
                <p>
                  Tix can automatically generate season ticket renewal orders that
                  include the seats held by the subscriber for the previous season.
                  Renewal expiration dates and times can be pre-configured to release
                  season tickets that have not been renewed by a specified date and
                  time. The Tix system can be used to generate customized season
                  ticket renewal letters as well as season ticket renewal e-mails.
                  Customers can renew tickets online by entering their Tix order
                  number, Renewal Code and credit card payment information. Box office
                  users can renew season ticket orders via order number, customer
                  lookup (name, phone number, e-mail address, etc.), or seat location
                  and applying any applicable payment method.
                </p>
              </div>
          </Modal>

          <Modal title='Analytics' 
            open={modalOpen === '10'} 
            onCancel={() => setModalOpen(null)} 
            footer={[
              <Button key='back' onClick={() => setModalOpen(null)}>
                Close
              </Button>
            ]}> 
            <div className='modal-body'>
              <p>
                Tix features a robust and sophisticated reporting suite that can
                provide organizations with the type of data needed for in-depth
                financial and marketing analysis.
              </p>
              <p>
                <strong>Extensive</strong> - View the data you need, when you need
                it. Tix offers a full suite of over 40 reports to provide you with
                ticket sales data in real-time.
              </p>
              <p>
                <strong>Customizable</strong> - View report data that is important
                to you. Tix reports are highly customizable to allow custom
                filtering, sorting, grouping, as well as the ability to customize
                the columns displayed and the sequence of the columns displayed.
                Report customization can be configured by the user and saved for
                later re-use.
              </p>
              <p>
                <strong>Exportable</strong> - Easily export sales and patron data to
                Excel, PDF, or CSV.
              </p>
              <p>
                Tix can be configured to include Google Analytics tracking codes for
                conversion tracking.
              </p>
            </div>
          </Modal>

          <Modal title='Barcode Scanning' 
            open={modalOpen === '11'} 
            onCancel={() => setModalOpen(null)} 
            footer={[
              <Button key='back' onClick={() => setModalOpen(null)}>
                Close
              </Button>
            ]}>   
            <div className='modal-body'>
              <p>Tix features flexible barcode scanning solutions:</p>
              <p>
                <strong>
                  Use the TixScan application for real-time barcode scanning and
                  access control
                </strong>{' '}
                - TixScan shines at high volume events where quick scanning and long
                battery life is a priority and where scanning is occurring in low
                light conditions. TixScan can be used on iOS devices (iPhone, iPod,
                iPad) running iOS 9 and higher; and Android devices running Android
                version 4 and higher. TixScan is free to use and can be downloaded
                from the Apple App Store, or the Google Play Store.
              </p>
              <p>
                <strong>Wired USB Barcode Scanning</strong> - USB barcode scanners
                plug into a laptop or PC and interface with the Tix E-Ticket
                scanning program. USB barcode scanners are easy to use and provide a
                cost-effective solution for access control.
              </p>
            </div>
          </Modal>

          <Modal title='Promote Your Brand' 
            open={modalOpen === '12'} 
            onCancel={() => setModalOpen(null)} 
            footer={[
              <Button key='back' onClick={() => setModalOpen(null)}>
                Close
              </Button>
            ]}>
            <div className='modal-body'>
              <p>
                While some services include a branded banner at the top of the
                ticketing pages, Tix wraps your web page around our ticketing engine
                which includes your logos, colors, graphics, navigation, sponsors,
                etc. This provides a seamless integration of ticket sales
                functionality with your website, resulting in an improved customer
                experience where additional information about your organization is
                only a click away. Our mobile-optimized ticket sales pages can also
                be branded with your organization's logo and colors.
              </p>
              <p>
                Print-at-home tickets can be customized to include your
                organization's logos and colors, as well as venue driving
                directions, a map of the venue location and event-specific
                advertising.
              </p>
            </div>
          </Modal>

          <Modal 
            title={<div className='modal-header' style={{paddingBottom: 15}}>
                <span className='fa-layers' style={{marginRight: 50}}>
                  <FontAwesomeIcon icon='fa-solid fa-circle' fontSize={54} color={'#171717'} />
                  <FontAwesomeIcon icon='fa-solid fa-envelope' fontSize={32} color={'#FEC503'} style={{marginLeft: 11}} />
                </span>
              <h4 className='modal-title' style={{color: '#333'}}>
                <span className={cn({hidden: modalOpen !== 'getStarted'})}>Get Started</span>
                <span className={cn({hidden: modalOpen !== 'learnMore'})}>Learn More</span>
                <span className={cn({hidden: modalOpen !== 'freeSetup'})}>Free Setup</span>
              </h4>
            </div>}
            open={modalOpen === 'getStarted' || modalOpen === 'learnMore' || modalOpen === 'freeSetup'} 
            onCancel={() => setModalOpen(null)} 
            footer={null}>
            <HorizontalSpinner className={cn({hidden: !infoRequestLoading})} />
            <div className={cn({hidden: infoRequestLoading})}>
              <form onSubmit={handleSubmit((data) => submitInfoRequest(data))}>
                <div className='modal-content'>         
                  <div className='modal-body'>
                    <div className='form-horizontal'>
                      <span className={cn({hidden: modalOpen !== 'getStarted'})}>
                        <strong>Start selling tickets today!</strong>
                        <br />
                        Give us a call at (800) 504-4849 or enter your contact information
                        below and we will contact you shortly.
                        <br />
                        <br />
                      </span>
                      <span className={cn({hidden: modalOpen !== 'learnMore'})}>
                        <strong>Learn more today!</strong>
                        <br />
                        Give us a call at (800) 504-4849 or enter your contact information
                        below and we will contact you shortly to answer any question you
                        may have about using our system.
                        <br />
                        <br />
                      </span>
                      <span className={cn({hidden: modalOpen !== 'freeSetup'})}>
                        <strong>Free Setup!</strong>
                        <br />
                        Give us a call at (800) 504-4849 or enter your contact information
                        below and we will contact you shortly.
                        <br />
                        <br />
                      </span>
                      
                      <label>
                        <span
                          style={{
                            color: '#b50000',
                            fontWeight: 'bold',
                          }}>
                          * Required Field
                        </span>
                      </label>
                      <div className='form-group required'>
                        <label
                          htmlFor='FirstName'
                          style={{
                            fontWeight: 'bold',
                          }}>
                          First Name{' '}
                          <span
                            style={{
                              color: '#b50000',
                            }}>
                            *
                          </span>
                        </label>
                        <input
                          className='form-control'
                          id='FirstName'
                          maxLength='50'
                          name='FirstName'
                          placeholder='First Name'
                          required
                          type='text'
                          {...register('FirstName', { required: true })}
                        />
                      </div>
                      <div className='form-group required'>
                        <label
                          htmlFor='LastName'
                          style={{
                            fontWeight: 'bold',
                          }}>
                          Last Name{' '}
                          <span
                            style={{
                              color: '#b50000',
                            }}>
                            *
                          </span>
                        </label>
                        <input
                          className='form-control'
                          id='LastName'
                          maxLength='50'
                          name='LastName'
                          placeholder='Last Name'
                          required
                          type='text'
                          {...register('LastName', { required: true })}
                        />
                      </div>
                      <div className='form-group required'>
                        <label
                          htmlFor='EMailAddress'
                          style={{
                            fontWeight: 'bold',
                          }}>
                          Email Address{' '}
                          <span
                            style={{
                              color: '#b50000',
                            }}>
                            *
                          </span>
                        </label>
                        <input
                          className='form-control'
                          id='EMailAddress'
                          maxLength='100'
                          name='EMailAddress'
                          placeholder='Email Address'
                          required
                          type='email'
                          {...register('EMailAddress', { required: true })}
                        />
                      </div>
                      <div className='form-group required'>
                        <label
                          htmlFor='PhoneNumber'
                          style={{
                            fontWeight: 'bold',
                          }}>
                          Phone Number{' '}
                          <span
                            style={{
                              color: '#b50000',
                            }}>
                            *
                          </span>
                        </label>
                        <input
                          className='form-control'
                          id='PhoneNumber'
                          maxLength='20'
                          name='PhoneNumber'
                          placeholder='Phone Number'
                          required
                          type='text'
                          {...register('PhoneNumber', { required: true })}
                        />
                      </div>
                      <div className='form-group'>
                        <label
                          htmlFor='Organization'
                          style={{
                            fontWeight: 'bold',
                          }}>
                          Organization Name{' '}
                          <span
                            style={{
                              color: '#b50000',
                            }}>
                            *
                          </span>
                        </label>
                        <input
                          className='form-control'
                          id='Organization'
                          maxLength='200'
                          name='Organization'
                          placeholder='Your Organization Name'
                          type='text'
                          required
                          {...register('Organization', { required: true })}
                        />
                      </div>
                      { modalOpen === 'learnMore' &&
                      <div
                        className='form-group priority2Item'>
                        <label
                          htmlFor='addQuestion'
                          style={{
                            fontWeight: 'bold',
                          }}>
                          Do you have a specific area of interest?
                        </label>
                        <textarea
                          className='form-control'
                          id='addQuestion'
                          name='AddQuestion'
                          rows='3'
                          {...register('AddQuestion')}
                        />
                      </div>
                      }
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: '#f1f1f1',
                      marginTop: 15
                    }}>
                    <div
                      style={{
                        paddingLeft: '10px',
                        paddingTop: '10px',
                        textAlign: 'left',
                      }}>
                      If you are interested in purchasing tickets,{' '}
                      <a
                        className='goldLink'
                        href='/search'>
                        click here.
                      </a>
                      <br />
                      If you have a question about tickets you purchased,{' '}
                      <a
                        className='goldLink'
                        href='mailto:customerservice@tix.com'>
                        click here.
                      </a>
                      <br />
                    </div>
                    <div className='form-group actions'>
                      <div
                        id='example-container'
                        style={{
                          textAlign: 'center',
                        }}
                      />
                      <div
                        style={{
                          textAlign: 'center',
                          paddingTop: 10,
                          paddingBottom: 10}}>
                          <Turnstile
                            sitekey={turnstileKey}
                            refreshExpired='auto'
                            appearance='interaction-only'
                            onVerify={(token) => {
                              setTurnstileToken(token)
                            }}
                            onTimeout={() => {
                              if (turnstile) {
                                turnstile.reset()
                              }
                            }}
                          />

                        <button
                          type='submit'
                          className='btn btn-confirm'>
                            Submit
                          </button>
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </Modal>

        <div className='page-wrapper'>
          <a className='skip-to-content' href='#skiplink'>
            Skip to main content
          </a>
          <div
            className='header-top dark'
            style={{
              backgroundColor: '#222',
              textAlign: 'right'
            }}>
            <div className='container'>
              <div className='row'>
                <div className='col-md-12 col-xs-12 '>
                  <div className='clearfix' id='header-top-second'>
                    <div className='header-top-dropdown pull-right'>
                      <div className='btn-group dropdown'>
                        <a
                          className='btn'
                          href='/ticket-sales/tix/1/profile'
                          style={{
                            color: '#FEC503',
                            fontSize: '14px',
                            fontWeight: 'bold',
                          }}
                          type='button'>
                          Ticket Buyer Login
                        </a>
                        <a
                          className='btn'
                          href='https://manage.tix.com/identity/login'
                          style={{
                            color: '#FEC503',
                            fontSize: '14px',
                            fontWeight: 'bold',
                          }}
                          type='button'>
                          Tix Client Login
                        </a>
                        <a
                          className='btn'
                          href='/search'
                          id='btnSearch'
                          style={{
                            color: '#FEC503',
                            fontSize: '14px',
                            fontWeight: 'bold',
                          }}
                          type='button'>
                          <FontAwesomeIcon icon='fa-solid fa-magnifying-glass' style={{paddingRight: 5}} />
                           Buy Tickets
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <header
            className='header dark fixed clearfix  dark-bg'
            style={{
              alignContent: 'center',
            }}>
            <div className='container'>
              <div className='row'>
                <div className='col-md-3 d-none d-sm-none d-md-block'>
                  <div>
                    <div className='logo smooth-scroll'>
                      <a href='/#header-top'>
                        <img
                          alt='tix'
                          src='https://cdn-img.tix.com/images/TixLogo512InverseDropShadow.png'
                        />
                      </a>
                    </div>
                    <div className='site-slogan'>it's the ticket!</div>
                  </div>
                </div>
                <div className='col-xs-3 d-sm-block d-md-none'>
                  <div>
                    <div className='logo smooth-scroll'>
                      <a href='/#header-top'>
                        <br />
                        <img
                          alt='tix'
                          src='https://cdn-img.tix.com/images/TixLogo512InverseDropShadow.png'
                          style={{
                            width: '120.83px',
                          }}
                        />
                      </a>
                    </div>
                  </div>
                </div>
                <div className='col-md-9 '>
                  <div>
                    <div className='main-navigation animated'>
                      <nav
                        className='navbar navbar-expand-lg navbar-light'
                        role='navigation'
                        style={{
                          backgroundColor: '#222222',
                          padding: '0px',
                        }}>
                        <div className='container-fluid'>
                          <div
                            className='collapse navbar-collapse'
                            id='navbar-collapse-1'
                            style={{
                              backgroundColor: '#222222',
                              zIndex: '101',
                            }}>
                            <ul className='nav navbar-nav'>
                              <li className='nav-item'>
                                <a href='/#why' id='navWhy' style={{fontWeight: 'bold', fontSize: 14}}>
                                  WHY TIX?
                                </a>
                              </li>
                              <li className='nav-item'>
                                <a
                                  href='/#features'
                                  id='navFeatures' style={{fontWeight: 'bold', fontSize: 14}}>
                                  FEATURES
                                </a>
                              </li>
                              <li className='nav-item'>
                                <a
                                  href='/#pricing'
                                  id='navPricing' style={{fontWeight: 'bold', fontSize: 14}}>
                                  PRICING
                                </a>
                              </li>
                              <li className='nav-item'>
                                <a
                                  href='/#serve'
                                  id='navWhoWeServe' style={{fontWeight: 'bold', fontSize: 14}}>
                                  WHO WE SERVE
                                </a>
                              </li>
                              <li className='nav-item'>
                                <a
                                  href='/ticketing-blog'
                                  id='navTixBlog' style={{fontWeight: 'bold', fontSize: 14}}>
                                  TIX BLOG
                                </a>
                              </li>
                              <li className='nav-item'>
                                <a
                                  onClick={() => setModalOpen('getStarted')}
                                  style={{fontWeight: 'bold', fontSize: 14, cursor: 'pointer'}}>
                                  GET STARTED
                                </a>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
              <div id='skiplink' tabIndex='-1' />
            </div>
          </header>
          <div className='banner' style={{borderBottom: '1px solid #333'}}>
            <div
              className='jumbotron background-image'
              style={{
                padding: '48px',
              }}>
              <div className='container text-center'>
                <h3 className='dark-text text-shadow1 h1'>
                  Bringing success to your events
                </h3>
                <h1 className='dark-text text-shadow2W h3'>
                  State-of-the-art, cloud-based event ticketing.
                </h1>
                <h2 className='dark-text text-shadow2W'>
                  Find out why thousands of organizations choose Tix
                  <br />
                  for their box office and online ticket sales.
                </h2>
                <button
                  className='btn-demo openModal'
                  onClick={() => setModalOpen('freeSetup')}
                  style={{width: 210}}
                  id='btnFreeSetup'>
                  Free Setup
                </button>
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-around',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    maxWidth: '500px',
                    paddingTop: '35px',
                  }}>
                  <a
                    href='https://www.capterra.com/reviews/9322/Tix?utm_source=vendor&utm_medium=badge&utm_campaign=capterra_reviews_badge'
                    target='_blank'>
                    <img
                      border='0'
                      src='https://assets.capterra.com/badge/122f66800f163085bc146c1bb1fbe362.svg?v=2002671&p=9322'
                      width='150px'
                    />
                  </a>
                  <a
                    href='https://www.getapp.com/customer-management-software/a/tix/reviews/'
                    style={{
                      marginLeft: '10px',
                      marginRight: '10px',
                    }}
                    target='_blank'>
                    <img
                      border='0'
                      src='https://www.getapp.com/ext/reviews_widget/v1/dark/tix-application'
                      width='150px'
                    />
                  </a>
                  <a
                    href='https://www.softwareadvice.com/event-management/tix-profile/reviews/'
                    target='_blank'>
                    <img
                      border='0'
                      src='https://badges.softwareadvice.com/reviews/b3414824-3419-4415-b6de-a6d200b70c43'
                      style={{
                        width: '150px',
                      }}
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className='section yellow-bg clearfix'>
            <div className='row'>
              <div className='.d-none .d-lg-block'></div>
            </div>

          <div style={{paddingTop: 35, paddingBottom: 35, backgroundColor: '#FEC503'}}>
            <Carousel
              swipeable={true}
              draggable={true}
              showDots={false}
              responsive={responsive}
              infinite={true}
              autoPlay={true}
              autoPlaySpeed={3500}
              keyBoardControl={true}  
              removeArrowOnDeviceType={['tablet', 'mobile']}>
                <div className='item testimonial' style={{backgroundColor: '#FEC503'}}>
                  <div className='row'>
                    <div className='col-md-12 text-center'>
                      <h2 className='title text-shadow3' style={{fontSize: 28}}>
                        THE TOOLS TO MAKE YOUR JOB EASIER
                      </h2>
                      <div className='testimonial-body'>
                        <div style={{display: 'flex', justifyContent: 'center'}}>
                          <FontAwesomeIcon icon='fa-solid fa-quote-left' style={{fontSize: 50, color: '#333', opacity: '0.75'}} />
                          <p className='quote text-shadow3' style={{paddingLeft: 10, paddingRight: 10, paddingTop: 15}}>
                          Our box office runs more efficiently and smoothly since we
                          switched to Tix.
                          </p>
                          <FontAwesomeIcon icon='fa-solid fa-quote-right' style={{fontSize: 50, color: '#333', opacity: '0.75'}} />
                        </div>
                        <div className='testimonial-info-1'>- Kristin Ramsey</div>
                        <div className='testimonial-info-2'>Ball State University</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='item testimonial'>
                  <div className='row'>
                    <div className='col-md-12 text-center'>
                      <h2 className='title text-shadow3' style={{fontSize: 28}}>
                        WE'RE THERE WHEN YOU NEED US
                      </h2>
                      <div className='testimonial-body'>
                        <div style={{display: 'flex', justifyContent: 'center'}}>
                          <FontAwesomeIcon icon='fa-solid fa-quote-left' style={{fontSize: 50, color: '#333', opacity: '0.75'}} />
                          <p className='quote text-shadow3' style={{paddingLeft: 10, paddingRight: 10, paddingTop: 15}}>
                            Tix offers the most friendly & efficient customer service in
                            the ticketing industry.
                          </p>
                          <FontAwesomeIcon icon='fa-solid fa-quote-right' style={{fontSize: 50, color: '#333', opacity: '0.75'}} />
                        </div>
                        <div className='testimonial-info-1'>- Joshua Westwick</div>
                        <div className='testimonial-info-2'>
                          South Dakota State University
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='item testimonial'>
                  <div className='row'>
                    <div className='col-md-12 text-center'>
                      <h2 className='title text-shadow3' style={{fontSize: 28}}>
                        A SYSTEM DESIGNED WITH YOU IN MIND
                      </h2>
                      <div className='testimonial-body'>
                        <div style={{display: 'flex', justifyContent: 'center'}}>
                          <FontAwesomeIcon icon='fa-solid fa-quote-left' style={{fontSize: 50, color: '#333', opacity: '0.75'}} />
                          <p className='quote text-shadow3' style={{paddingLeft: 10, paddingRight: 10, paddingTop: 15}}>
                            Tix has provided a first class ticketing system for our
                            organization.
                          </p>
                          <FontAwesomeIcon icon='fa-solid fa-quote-right' style={{fontSize: 50, color: '#333', opacity: '0.75'}} />
                        </div>
                        <div className='testimonial-info-1'>- Sue Talford</div>
                        <div className='testimonial-info-2'>Boerne Performing Arts</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='item testimonial'>
                  <div className='row'>
                    <div className='col-md-12 text-center'>
                      <h2 className='title text-shadow3' style={{fontSize: 28}}>
                        ELIMINATE THE BARRIERS TO YOUR SUCCESS
                      </h2>
                      <div className='testimonial-body'>
                        <div style={{display: 'flex', justifyContent: 'center'}}>
                          <FontAwesomeIcon icon='fa-solid fa-quote-left' style={{fontSize: 50, color: '#333', opacity: '0.75'}} />
                          <p className='quote text-shadow3' style={{paddingLeft: 10, paddingRight: 10, paddingTop:15}}>
                          We've had a wonderful experience with Tix. It's easy and our
                          ticket buyers love the convenience.
                          </p>
                          <FontAwesomeIcon icon='fa-solid fa-quote-right' style={{fontSize: 50, color: '#333', opacity: '0.75'}} />
                        </div>
                        <div className='testimonial-info-1'>- Gaye Wutzke</div>
                        <div className='testimonial-info-2'>Mid-Columbia Ballet</div>
                      </div>
                    </div>
                  </div>
                </div>
              </Carousel>
            </div>

          </div>
        </div>
        <section
          className='main-container object-non-visible animated object-visible fadeInUpSmall'
          data-animation-effect='fadeInUpSmall'
          data-effect-delay='300'>
          <div className='section clearfix' style={{paddingTop: 25}}>
            <div className='container'>
              <div
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-around',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}>
                    <a href="https://www.softwareadvice.com/ticketing/#frontrunners"> <img border="0" width="150" src="https://brand-assets.softwareadvice.com/badge/a03d9dc0-7929-470e-94b6-8180e54136ce.svg" /> </a>
                    <a href="https://www.capterra.com/ticketing-software/shortlist"> <img border="0" width="150" src="https://brand-assets.capterra.com/badge/569697c6-7d74-4f2f-a05c-048be535200a.svg" /> </a>
                    <a title="Tix is a leader in Event Registration & Ticketing on G2" href="https://www.g2.com/products/tix/reviews?utm_source=rewards-badge "><img style={{width: 125}} alt="Tix is a leader in Event Registration & Ticketing on G2" src="https://images.g2crowd.com/uploads/report_medal_translation/image/4187/medal.svg" /></a>
                    <a href="https://www.getapp.com/customer-management-software/ticketing/category-leaders"> <img border="0"  width="150" src="https://brand-assets.getapp.com/badge/d6aac9b9-6704-4d9e-90bd-a2c08e63899e.png" /> </a>
                    <a href="https://www.capterra.com/p/9322/Tix/"> <img border="0" width="150" src="https://brand-assets.capterra.com/badge/f34c87d7-2f34-47e9-ac83-24bd5ded4f6d.png" /> </a>
              </div>
              <h2 className='text-center space-top' id='why'>
                Why Tix?
              </h2>
              <div className='separator' />
              <p className='lead text-center' />
              <br />
              <div className='row'>
                <div className='col-md-6'>
                  <h2 className='title'>Your Complete Ticketing Solution</h2>
                  <div className='row'>
                    <div className='col-md-6'>
                      <img
                        style={{display: 'block', maxWidth: '100%', height: 'auto'}}
                        alt=''
                        src='https://cdn-pl.tix.com/clients/tix/mainsite/images/TixLogoComplete.gif'
                      />
                    </div>
                    <div className='col-md-6'>
                      <p style={{color: '#666666'}}>
                        Tix is a state-of-the-art, cloud-based ticketing system that
                        features fully integrated access controls, event management
                        controls, multi-channel distribution capabilities and a robust
                        reporting suite. Tix provides a customer-facing ticket sales
                        page, as well as inventory controls, invoicing and financial
                        accountability.
                      </p>
                    </div>
                  </div>
                  <p style={{color: '#666666'}}>
                    We specialize in no-cost, feature-rich ticketing solutions for
                    venues, promoters, producers, universities, theme parks, tours,
                    museums, casinos, theatres, film festivals, concerts, night clubs,
                    music festivals, race tracks and more.
                  </p>
                  <div className='space .d-md-none .d-lg-none' />
                </div>
                <div className='col-md-6'>
                  <Collapse items={items} defaultActiveKey={['1']} styles={{contentBg: 'black'}} />
                </div>
              </div>
            </div>
          </div>
          <div className='section gray-bg clearfix' style={{marginTop: 30}}>
            <div className='container'>
              <br /><br />
              <h2 className='text-center space-top' id='features'>
                Features
              </h2>
              <div className='separator' />
              <p className='lead text-center'>
                Tix features an easy-to-use interface, a robust reporting suite,
                marketing tools to help keep your customers engaged and the ability
                for you to access all of your ticket sales and donation information in
                one place.
                <br />
                It's ticketing software designed with you in mind.
              </p>
              <br />
              <div className='row grid-space-10'>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'
                    data-animation-effect='fadeInUpSmall'
                    data-effect-delay='0'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <FontAwesomeIcon icon='fa-solid fa-gears' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>Easy Integration</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        Tix seamlessly integrates into your box office operations. The
                        Tix system is easy to learn and teach - you'll have your box
                        office staff trained in no time. Online ticket sales take
                        place on a private label page that is designed to look and
                        feel like your website. It can be customized to have the same
                        look and feel of your existing web site, complete with your
                        logos, banners, links, fonts and colors.
                      </p>
                      <Button
                        aria-label='Learn more about Easy Integration'
                        id='featureEasyIntegration'
                        onClick={() => setModalOpen('1')}
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'
                    data-animation-effect='fadeInUpSmall'
                    data-effect-delay='200'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <FontAwesomeIcon icon='fa-solid fa-ticket' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>Sell Tickets</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        Tix can accommodate orders taken by Phone, Fax, Mail, Box
                        Office and Internet. You can even sell tickets directly
                        through your Facebook Page! You can use any combination of our
                        services to sell your event tickets.
                      </p>
                      <Button
                        aria-label='Learn more about sell tickets'
                        id='featureSellTickets'
                        onClick={() => setModalOpen('2')}
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'
                    data-animation-effect='fadeInUpSmall'
                    data-effect-delay='400'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <FontAwesomeIcon icon='fa-solid fa-chair' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>Multiple Seating Options</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        We support reserved seating, general admission seating and can
                        handle events with combination reserved and general admission
                        seating. We program your seating charts for you at absolutely
                        no cost.
                      </p>
                      <Button
                        aria-label='Learn more about Multiple seating options'
                        id='featureSeatingOptions'
                        onClick={() => setModalOpen('3')}
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className='row grid-space-10'>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <FontAwesomeIcon icon='fa-solid fa-users' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>CRM</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        Enhance the relationship you have with your ticket buyers
                        using our suite of customer reports, Email marketing tools and
                        customer history tracking. Our CRM tools will help your
                        organization understand and grow your patron base.
                      </p>
                      <Button
                        aria-label='Learn more about CRM'
                        id='featureCRM'
                        onClick={() => setModalOpen('4')}
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <FontAwesomeIcon icon='fa-solid fa-comments' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>Engage Your Patrons</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        Use our tools and reports to create ongoing, meaningful
                        interactions with your patrons. Know who your patrons are and
                        generate targeted customer lists to use in your marketing
                        efforts.{' '}
                      </p>
                      <Button
                        id='featureEngagePatrons'
                        onClick={() => setModalOpen('5')}
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <FontAwesomeIcon icon='fa-solid fa-envelope' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>Email Marketing</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        Email your patrons directly at any time through Tix. Contact
                        them to let them know about an upcoming event or simply thank
                        them for purchasing tickets to a past event. Tix helps
                        organizations like yours build strong relationships with your
                        patrons.
                      </p>
                      <Button
                        aria-label='Learn more about email marketing'
                        id='featureEmailMarketing'
                        onClick={() => setModalOpen('6')}
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className='row grid-space-10'>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <FontAwesomeIcon icon='fa-solid fa-gift' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>Donations/Memberships</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        Tix can be used to process donations or membership sales at
                        the various giving/membership levels allowed by your
                        organization. Tix can be programmed to give discounts to
                        ticket buyers based on their purchase history, including
                        donations and memberships.
                      </p>
                      <Button
                        aria-label='Learn more about donations/memberships'
                        id='featureDonations'
                        onClick={() => setModalOpen('7')}
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <i className='fa fa-mobile' />
                      <FontAwesomeIcon icon='fa-solid fa-mobile' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>Mobile Box Office</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        Take Tix with you on your iPad, iPhone or other mobile device
                        and sell tickets without needing to install an app. Use Tix to
                        process real-time ticket sales and accept credit card payments
                        on your mobile device.{' '}
                      </p>
                      <Button
                        aria-label='Learn more about mobile box office'
                        id='featureMobileBoxOffice'
                        onClick={() => setModalOpen('8')}
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <i className='fa fa-calendar' />
                      <FontAwesomeIcon icon='fa-solid fa-calendar' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>Season Packages</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        Tix can handle most any season ticket or package need,
                        including 'build-your-own' or fixed packages. Additionally, we
                        offer same-seat season package renewals so that your patrons
                        can purchase the seats they held the previous season.
                      </p>
                      <Button
                        aria-label='Learn more about season packages'
                        id='featureSubscriptions'
                        onClick={() => setModalOpen('9')}
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className='row grid-space-10'>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <i className='fa fa-line-chart' />
                      <FontAwesomeIcon icon='fa-solid fa-line-chart' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>Analytics</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        Tix includes a variety of reporting tools which allow
                        authorized users to separate data by event, event type, event
                        category, method of payment, order type, cashier, as well as
                        by event date range and transaction date range.
                      </p>
                      <Button
                        aria-label='Learn more about analytics'
                        id='featureAnalytics'
                        onClick={() => setModalOpen('10')}
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <i className='fa fa-qrcode' />
                      <FontAwesomeIcon icon='fa-solid fa-qrcode' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>Barcode Scanning</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        Tix offers real-time barcode scanning through its proprietary
                        TixScan app on iOS devices. Ticket validation can be performed
                        on E-Tickets (Print-at-Home) and/or hard tickets for
                        validation and attendance reporting.
                      </p>
                      <Button
                        aria-label='Learn more about barcode scanning'
                        id='featureBarcodeScanning'
                        onClick={() => setModalOpen('11')}
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
                <div className='col-sm-4'>
                  <div
                    className='box-style-2 object-non-visible'>
                    <div aria-hidden='true' className='icon-container default-bg'>
                      <i className='fa fa-bullhorn' />
                      <FontAwesomeIcon icon='fa-solid fa-bullhorn' style={{fontSize: 36}} />
                    </div>
                    <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                      <h2 style={{marginTop: -60, fontSize: 24}}>Promote Your Brand</h2>
                      <p style={{color: '#666666', fontSize: 15}}>
                        Our private label solution seamlessly integrates ticket
                        purchasing into your website. It can be customized to have the
                        same look and feel of your existing web site, complete with
                        your logos, banners, links, fonts and colors.
                      </p>
                      <Button
                        aria-label='Learn more about promoting your brand'
                        onClick={() => setModalOpen('12')}
                        id='featurePromoteBrand'
                        style={{
                          cursor: 'pointer',
                        }}>
                        Learn More
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='section parallax parallax-bg dark-translucent-bg'>
              <div className='call-to-action' style={{textAlign: 'center', color: 'white'}}>
                <h2>Safe & Secure</h2>
              </div>
              <Carousel
                swipeable={true}
                draggable={true}
                showDots={false}
                responsive={responsive}
                infinite={true}
                autoPlay={true}
                autoPlaySpeed={3500}
                keyBoardControl={true}  
                removeArrowOnDeviceType={['tablet', 'mobile']}>
                <div className='testimonial-safe'>
                  <div className='container'>
                    <div className='row'>
                      <div className='col-md-12'>
                        <h2 className='title'>SECURITY IS TOP PRIORITY AT TIX</h2>
                        <div className='testimonial-safe-body'>
                          <p>
                            Without the confidence that their information is secure,
                            ticket buyers simply won't buy tickets.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='testimonial-safe'>
                    <div className='container'>
                      <div className='row'>
                        <div className='col-md-12'>
                          <h2
                            className='title'
                            style={{
                              textTransform: 'uppercase',
                            }}>
                            Payment Card Industry (PCI) Level 1 Certified.
                          </h2>
                          <div className='testimonial-safe-body'>
                            <p>
                              PCI Level 1 is the highest (most secure) level of
                              certification available from the Payment Card Industry.{' '}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='testimonial-safe'>
                    <div className='container'>
                      <div className='row'>
                        <div className='col-md-12'>
                          <h2
                            className='title'
                            style={{
                              textTransform: 'uppercase',
                            }}>
                            128 bit Secure Sockets Layer (SSL) Encryption
                          </h2>
                          <div className='testimonial-safe-body'>
                            <p>
                              Tix employs 128 bit Secure Sockets Layer (SSL)
                              Encryption to make sure that sensitive information is
                              secure.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='testimonial-safe'>
                    <div className='container'>
                      <div className='row'>
                        <div className='col-md-12'>
                          <h2
                            className='title'
                            style={{
                              textTransform: 'uppercase',
                            }}>
                            Visa Global Registry of Service Providers
                          </h2>
                          <div className='testimonial-safe-body'>
                            <p>
                              Inclusion in Visa's Registry requires that Tix meet
                              Visa's program requirements, rules and applicable PCI
                              requirements.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='testimonial-safe'>
                    <div className='container'>
                      <div className='row'>
                        <div className='col-md-12'>
                          <h2
                            className='title'
                            style={{
                              textTransform: 'uppercase',
                            }}>
                            Credit Card Number Storage
                          </h2>
                          <div className='testimonial-safe-body'>
                            <p>
                              Tix does not store full credit card numbers. We only
                              store the last four digits of the credit card for
                              reference. This means that even if a hacker could gain
                              unauthorized access to our system, there wouldn't be any
                              valuable credit card information to steal.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='testimonial-safe'>
                    <div className='container'>
                      <div className='row'>
                        <div className='col-md-12'>
                          <h2
                            className='title'
                            style={{
                              textTransform: 'uppercase',
                            }}>
                            Nevada Gaming Commission
                          </h2>
                          <div className='testimonial-safe-body'>
                            <p>
                              Tix is one of very few ticketing companies that have met
                              the stringent requirements of the Nevada Gaming
                              Commission for use in Nevada gaming establishments with
                              entertainment venues and ticket sales.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
              </Carousel>


              <div
                style={{
                  textAlign: 'center',
                }}>
                <img
                  alt='PCI-DSS Level 1 Certified, Hacker Safe, Trustwave - Trusted Commerce, Authorize.net'
                  border='0'
                  src='https://cdn-img.tix.com/images/security1.png'
                  style={{
                    display: 'inline-block',
                  }}
                />
              </div>
          </div>
          <div className='section'>
            <div className='container'>
              <br />
              <h2 className='text-center space-top' id='pricing'>
                Pricing
              </h2>
              <div className='separator' />
              <p className='lead text-center'>
                No set up fees. No monthly or annual fees. No hidden fees.
                <br />
                No risk.
              </p>
              <div className='row'>
                <div className='col-md-12'>
                  <div className='row'>
                    <div className='col-sm-6'>
                      <div
                        className='box-style-1 default-bg object-non-visible' style={{paddingTop: 20, paddingBottom: 20, margin: '20px 0px', backgroundColor: '#fec503', color: '#333', textAlign: 'center'}}>
                        <div style={{fontSize: 48, fontWeight: 700, color: '#333', textShadow: '2px 2px 10px #909090'}}>$0.25</div>
                        <p>Per Ticket</p>
                        <h2>Box Office Fee</h2>
                        <p style={{color: '#666666'}}>
                          Tickets sold by your organization through our box office
                          system. (Walk-up sales, etc.)
                        </p>
                        <FontAwesomeIcon icon='fa-solid fa-user' style={{fontSize: 65}} />
                      </div>
                    </div>
                    <div className='col-sm-6'>
                      <div
                        className='box-style-1 default-bg object-non-visible' style={{paddingTop: 20, paddingBottom: 20, margin: '20px 0px', backgroundColor: '#fec503', color: '#333', textAlign: 'center'}}>
                        <div style={{fontSize: 48, fontWeight: 700, color: '#333', textShadow: '2px 2px 10px #909090'}}>$1.50</div>
                        <p>Per Ticket</p>
                        <h2>Online Fee</h2>
                        <p style={{color: '#666666'}}>
                          Tickets sold online, through desktop and mobile devices.
                        </p>
                        <FontAwesomeIcon icon='fa-solid fa-desktop' style={{fontSize: 65}} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='container'>
            <div className='row'>
              <div className='col-md-12'>
                <div className='row grid-space-20'>
                  <div className='col-sm-6'>
                    <div
                      className='box-style-3 left object-non-visible'
                      data-animation-effect='fadeInUpSmall'
                      data-effect-delay='0'>
                      <div className='body'>
                        <h2 style={{fontSize: 20}}>Merchant Account Flexibility</h2>
                        <p style={{color: '#666666', fontSize: 15}}>
                          We offer two ways to process credit cards through our
                          ticketing system. You can either use your merchant account,
                          or ours. In order to use your own merchant account, it must
                          include a compatible e-commerce payment gateway. Tix is
                          compatible with over 30 different e-commerce payment
                          gateways including Authorize.net, Cybersource, Global
                          Gateway, Virtual Merchant, Stripe, PayPal and many more.{' '}
                        </p>
                      </div>
                    </div>
                    <div
                      className='box-style-3 left object-non-visible'
                      data-animation-effect='fadeInUpSmall'
                      data-effect-delay='200'>
                      <div></div>
                      <div className='body'>
                        <h2 style={{fontSize: 20}}>Credit Card Processing</h2>
                        <p style={{color: '#666666', fontSize: 15}}>
                          If you wish to use the Tix merchant account we charge an
                          additional fee of 5% of the ticket price for credit card
                          processing. Tix does not charge an additional fee if you use
                          your own merchant account.
                        </p>
                      </div>
                    </div>
                    <div
                      className='box-style-3 left object-non-visible'
                      data-animation-effect='fadeInUpSmall'
                      data-effect-delay='400'>
                      <div></div>
                      <div className='body'>
                        <h2 style={{fontSize: 20}}>Payment Schedule</h2>
                        <p style={{color: '#666666', fontSize: 15}}>
                          When using your merchant account for credit card processing,
                          ticket revenue goes directly into your account within 48-72
                          hours after each ticket purchase. We then invoice you for
                          our fees. When using our merchant account for credit card
                          processing, we send you a check the week after the event.{' '}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className='col-sm-6'>
                    <div
                      className='box-style-3 object-non-visible'
                      data-animation-effect='fadeInUpSmall'
                      data-effect-delay='0'>
                      <div></div>
                      <div className='body'>
                        <h2 style={{fontSize: 20}}>Set Your Own Fees</h2>
                        <p style={{color: '#666666', fontSize: 15}}>
                          All of our fees can be passed along to the ticket buyer.
                          This enables your organization to use our service at no
                          cost. You may also include some, or all, of our fees into
                          the ticket price. It's up to you.{' '}
                        </p>
                      </div>
                    </div>
                    <div
                      className='box-style-3 object-non-visible'
                      data-animation-effect='fadeInUpSmall'
                      data-effect-delay='200'>
                      <div></div>
                      <div className='body'>
                        <h2 style={{fontSize: 20}}>Ticket Printing & Mailing (optional)</h2>
                        <p style={{color: '#666666', fontSize: 15}}>
                          Tix can print and mail tickets directly to your ticket
                          buyers. There is a single $3.00 shipping and handling fee
                          applied to each order when we print and mail tickets for
                          you. We can also print tickets and mail them directly to you
                          for $.20 per ticket plus shipping. There are no shipping and
                          handling fees applied to Will Call orders or orders in which
                          we do not print and/or mail the tickets.{' '}
                        </p>
                      </div>
                    </div>
                    <div
                      className='box-style-3 object-non-visible'
                      data-animation-effect='fadeInUpSmall'
                      data-effect-delay='400'>
                      <div></div>
                      <div className='body'>
                        <h2 style={{fontSize: 20}}>Low, Low Service Fees</h2>
                        <p style={{color: '#666666', fontSize: 15}}>
                          Our service fees remain among the lowest in the ticketing
                          industry. Pass those savings along to your patrons and watch
                          your ticket sales increase!
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='space' />
          <div className='section gray-bg clearfix'>
            <div className='container'>
              <br />
              <h2 className='text-center space-top' id='serve'>
                Who We Serve
              </h2>
              <div className='separator' />
              <p className='lead text-center'>
                We specialize in feature-rich ticketing solutions for venues,
                promoters, producers, universities, theme parks, tours, museums,
                casinos, theatres, film festivals, concerts, night clubs, music
                festivals, race tracks and more.
              </p>
              <br />
              <div className='row'>
                <div className='col-md-12'>
                  <div className='row grid-space-10'>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='0'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-masks-theater' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Performing Arts</h2>
                          <ul className='list-icons'>
                            <li>Reserved seating with multiple price tiers</li>
                            <li>Season packages</li>
                            <li>CRM and email marketing</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='200'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-university' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Colleges and Universities</h2>
                          <ul className='list-icons'>
                            <li>CashNET and TouchNet ready</li>
                            <li>Usable across multiple departments</li>
                            <li>Student ID validation possible</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='400'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-trophy' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Sporting Events</h2>
                          <ul className='list-icons'>
                            <li>Photographic seat views</li>
                            <li>Season tickets and same-seat renewals</li>
                            <li>Group rates and discounts</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='row grid-space-10'>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='600'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-star' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Dance Schools</h2>
                          <ul className='list-icons'>
                            <li>Early event access for specific patrons</li>
                            <li>Customer-specific promotional codes</li>
                            <li>Real-time discount code tracking</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='800'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-guitar' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Live Music Venues</h2>
                          <ul className='list-icons'>
                            <li>E-tickets and barcode scanning</li>
                            <li>Multi-day and single day passes</li>
                            <li>Pre-sale codes</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='1000'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-diamond' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Casinos</h2>
                          <ul className='list-icons'>
                            <li>Nevada Gaming Commission approved</li>
                            <li>Customer classifications</li>
                            <li>Add-ons/Upsells</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='row grid-space-10'>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='600'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-film' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Film Festivals</h2>
                          <ul className='list-icons'>
                            <li>Member discounts</li>
                            <li>Festival passes and packages</li>
                            <li>Social media tie-ins</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='800'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-calendar' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Tours</h2>
                          <ul className='list-icons'>
                            <li>Calendar schedule display</li>
                            <li>Mobile optimized purchasing</li>
                            <li>Sell tickets from tablet</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='1000'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-heart' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Non-Profits</h2>
                          <ul className='list-icons'>
                            <li>Donations and memberships</li>
                            <li>Customized surveys</li>
                            <li>Email Marketing</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='row grid-space-10'>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='600'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-museum' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Museums</h2>
                          <ul className='list-icons'>
                            <li>Timed admissions</li>
                            <li>Member discounts</li>
                            <li>Special event access</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='800'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-mortar-board' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>High Schools</h2>
                          <ul className='list-icons'>
                            <li>Reserved seating</li>
                            <li>Student ticket discounts</li>
                            <li>Accept cash or check</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='1000'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-peace' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Religious Organizations</h2>
                          <ul className='list-icons'>
                            <li>Donation solicitations</li>
                            <li>Process box office sales on-site</li>
                            <li>Call center service</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='row grid-space-10'>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='600'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-tents' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Fairs and Festivals</h2>
                          <ul className='list-icons'>
                            <li>Gate admissions</li>
                            <li>Concert tickets</li>
                            <li>Group discounts</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='800'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-music' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Promoters</h2>
                          <ul className='list-icons'>
                            <li>Unlimited venues</li>
                            <li>Sell tickets from your iPhone or iPad</li>
                            <li>Real-time settlement reporting</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className='col-sm-4'>
                      <div
                        className='box-style-2 object-non-visible'
                        data-animation-effect='fadeInUpSmall'
                        data-effect-delay='1000'>
                        <div aria-hidden='true' className='icon-container default-bg'>
                          <FontAwesomeIcon icon='fa-solid fa-id-badge' style={{fontSize: 36}} />
                        </div>
                        <div className='body' style={{marginLeft: 80, paddingBottom: 20}}>
                          <h2 style={{marginTop: -60, fontSize: 24}}>Trade Shows/Exhibits</h2>
                          <ul className='list-icons'>
                            <li>Sign-Ups and registrations</li>
                            <li>Collect information for each attendee</li>
                            <li>Sell display booths and spaces</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <h3 className='text-center dark space-top' style={{fontSize: 22, fontWeight: 500, fontFamily: '"Open Sans", sans-serif'}}>...and many more!</h3>
                </div>
              </div>
            </div>
          </div>
          <div className='section parallax parallax-bg-2 dark-translucent-bg'>
            <div className='container'>
              <div className='call-to-action'>
                <h2 className='text-center space-top' id='start'>
                  Free Set Up
                </h2>
                <p>
                  Join thousands of venues, promoters and producers who have made Tix
                  their complete box office and online ticketing solution.
                </p>
                <button
                  className='btn-demo openModal'
                  onClick={() => setModalOpen('getStarted')}
                  style={{width: 210}}
                  id='btnFreeSetup'>
                  Get Started
                </button>
                <br />
                <p>
                  Not quite ready yet? Contact us to{' '}
                  <a
                    className='openModal'
                    onClick={() => setModalOpen('learnMore')}
                    id='lnkLearnMore'
                    style={{color: 'white', cursor: 'pointer'}}>
                    learn more
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </section>
        <footer id='footer' style={{fontFamily: '"Open Sans", sans-serif'}}>
          <div className='footer'>
            <div className='container'>
              <div className='row'>
                <div className='col-md-6'>
                  <div className='footer-content'>
                    <div className='logo-footer' />
                    <div className='row'>
                      <div className='col-sm-6'>
                        <p style={{fontSize: 14}}>
                          Integrated box office and online ticketing solutions for
                          thousands of organizations like yours.
                          <br />
                        </p>
                      </div>
                      <div className='col-sm-6'>
                        <ul className='list-icons' style={{listStyle: 'none', fontSize: 14}}>
                          <li>
                            <FontAwesomeIcon icon='fa-solid fa-map-marker' />{' '}
                            100 W. Broadway
                            <br />
                            Suite 3000
                            <br />
                            Long Beach, CA 90802
                          </li>
                          <li>
                            <FontAwesomeIcon icon='fa-solid fa-phone' />{' '}
                            800.504.4849{' '}
                          </li>
                          <li>
                            <FontAwesomeIcon icon='fa-solid fa-fax' />{' '}
                            562.951.1463{' '}
                          </li>
                          <li>
                            <FontAwesomeIcon icon='fa-solid fa-envelope' />{' '}
                            <a href='mailto:info@tix.com' id='footerEmailTix' className='goldLink'>
                              info@tix.com
                            </a>
                          </li>
                          <br />
                          <p
                            style={{
                              wordBreak: 'keep-all',
                            }}>
                            <strong>Need Order Help?</strong>
                            <br />
                            <a href='mailto:customerservice@tix.com' className='goldLink'>
                              customerservice@tix.com
                            </a>
                          </p>
                          <ul className='social-links circle'>
                            <li className='facebook'>
                              <a
                                href='https://www.facebook.com/pages/Tix/94873447107'
                                id='footerFacebook'
                                target='_blank'
                                title='Tix on Facebook'>
                                <FontAwesomeIcon icon='fa-brands fa-facebook' />
                              </a>
                            </li>
                            <li className='twitter'>
                              <a
                                href='http://www.twitter.com/TixInc'
                                id='footerTwitter'
                                target='_blank'
                                title='Tix on Twitter'>
                                <FontAwesomeIcon icon='fa-brands fa-x-twitter' />
                              </a>
                            </li>
                            <li className='instagram'>
                              <a
                                href='https://www.instagram.com/tix_inc/'
                                id='footerInstagram'
                                target='_blank'
                                title='Tix on Instagram'>
                                <FontAwesomeIcon icon='fa-brands fa-instagram' />
                              </a>
                            </li>
                          </ul>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-sm-6 col-md-2'>
                  <div className='footer-content'>
                    <h3 style={{fontSize: 22}}>Links</h3>
                    <nav style={{fontSize: 13}}>
                      <ul className='nav nav-pills flex-column'>
                        <li className='nav-item'>
                          <a
                            className='nav-link'
                            href='/#why'
                            id='footerWhyTix'>
                            WHY TIX?
                          </a>
                        </li>
                        <li className='nav-item'>
                          <a
                            className='nav-link'
                            href='/#features'
                            id='footerFeatures'>
                            FEATURES
                          </a>
                        </li>
                        <li className='nav-item'>
                          <a
                            className='nav-link'
                            href='/#pricing'
                            id='footerPricing'>
                            PRICING
                          </a>
                        </li>
                        <li className='nav-item'>
                          <a
                            className='nav-link'
                            href='/#serve'
                            id='footerWhoWeServe'>
                            WHO WE SERVE
                          </a>
                        </li>
                        <li className='nav-item'>
                          <a
                            className='nav-link'
                            href='/ticketing-blog'
                            id='footerTixBlog'>
                            TIX BLOG
                          </a>
                        </li>
                        <li className='nav-item'>
                          <a
                            className='nav-link'
                            href='/ticket-sales/tix/1/profile'
                            id='footerTicketBuyerLogin'>
                            TICKET BUYER LOGIN
                          </a>
                        </li>
                        <li className='nav-item'>
                          <a
                            className='nav-link'
                            href='https://manage.tix.com/identity/login'
                            id='footerTixClientLogin'>
                            TIX CLIENT LOGIN
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
                <div className='col-sm-6 col-md-3 col-md-offset-1'>
                  <div className='footer-content'>
                    <img
                      alt='tix'
                      id='LogoOnGray'
                      style={{maxWidth: 306}}
                      src='https://cdn-img.tix.com/images/TixLogoBGGray400x300R.png'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='subfooter'>
                <div style={{color: '#fff', paddingLeft: 10, float: 'left'}}><a target='_blank' href='http://www.tix.com' style={{color: '#fec503'}}>Tix, Inc.</a> &#169; 2001-2026 | All Rights Reserved</div>
                <div style={{float: 'right'}}>
                    <a style={{paddingRight: 10}} className='footerLink' href='/ticket-sales/privacypolicy'>Privacy Policy</a>
                    <a style={{paddingRight: 10}} className='footerLink' href='/ticket-sales/termsofuse'>Terms of Use</a>
                </div>
            </div>
        </footer>
      </div>
    </>
  )
}

export default Home
